import { useEffect, useRef } from 'react';
import { useAnalytics } from '@vaptmenu/analytics-react';
import { useAuthStore } from '@/features/auth/stores/auth-store';
import { setRealtimeDisconnectedListener } from '@/features/orders/hooks/useOrdersSignalR';

/**
 * Espelha o estado de autenticação no provider de analytics:
 *
 * - sessão criada OU restaurada do localStorage → identify com os dados de
 *   conta do operador (perfil B2B, design.md D7);
 * - tenant selecionado, trocado ou reidratado → setTenant;
 * - user removido (logout explícito ou 401 forçado em qualquer lugar) →
 *   reset, para a próxima sessão no mesmo navegador não herdar a identidade.
 *
 * Também é o assinante único da queda da conexão de tempo real: o hook do
 * SignalR é montado por mais de uma tela ao mesmo tempo, e emitir por
 * consumidor duplicaria o evento.
 */
export function AnalyticsIdentitySync() {
  const { identify, reset, setTenant, track } = useAnalytics();
  const user = useAuthStore((s) => s.user);
  const selectedTenant = useAuthStore((s) => s.selectedTenant);

  // reset só na transição autenticado → anônimo; resetar visitante anônimo
  // a cada carga da tela de login rotacionaria o id sem necessidade.
  const hadUserRef = useRef(false);
  useEffect(() => {
    if (user) {
      hadUserRef.current = true;
      identify(user.id, { email: user.email, name: user.name, role: user.role });
    } else if (hadUserRef.current) {
      hadUserRef.current = false;
      reset();
    }
  }, [user, identify, reset]);

  useEffect(() => {
    if (selectedTenant) {
      setTenant(selectedTenant.slug, { tenantId: selectedTenant.id });
    } else {
      setTenant(null);
    }
  }, [selectedTenant, setTenant]);

  useEffect(() => {
    setRealtimeDisconnectedListener((reason) =>
      track('panel.realtime_disconnected', { reason }),
    );
    return () => setRealtimeDisconnectedListener(null);
  }, [track]);

  return null;
}
